import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  Link as MuiLink,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeToggle } from "./ThemeToggle";
import { config } from "../config";

const NAV_ANCHORS = [
  { id: "services", key: "nav.services" },
  { id: "about", key: "nav.about" },
  { id: "gallery", key: "nav.gallery" },
  { id: "process", key: "nav.process" },
  { id: "testimonials", key: "nav.testimonials" },
  { id: "contact", key: "nav.contact" },
] as const;

export function Header() {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setDrawerOpen(false);
  };

  return (
    <AppBar position="sticky">
      <Container maxWidth="lg">
        <Toolbar
          disableGutters
          sx={{
            gap: 2,
            minHeight: { xs: 74, md: 84 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr auto", md: "minmax(220px, 1fr) auto auto" },
            alignItems: "center",
          }}
        >
          <MuiLink
            href="#top"
            underline="none"
            color="inherit"
            sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
          >
            <Typography
              variant="h5"
              component="span"
              sx={{
                fontFamily: '"Playfair Display", serif',
                letterSpacing: 0.6,
                fontWeight: 700,
                lineHeight: 1.05,
                fontSize: { xs: "1.55rem", md: "1.75rem" },
                whiteSpace: "normal",
                maxWidth: { xs: 148, md: 220 },
              }}
            >
              {config.brandName}
            </Typography>
          </MuiLink>

          {isDesktop ? (
            <>
              <Stack
                direction="row"
                spacing={0.7}
                alignItems="center"
                justifyContent="center"
                sx={{ px: 0.5 }}
              >
              {NAV_ANCHORS.map((item) => (
                <Button
                  key={item.id}
                  color="inherit"
                  onClick={() => scrollTo(item.id)}
                  sx={{
                    fontWeight: 500,
                    borderRadius: 999,
                    px: 1.35,
                    minWidth: "auto",
                    fontSize: "0.96rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t(item.key)}
                </Button>
              ))}
              <LanguageSwitcher />
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ pl: 0.3 }}>
                <ThemeToggle />
              </Stack>
            </>
          ) : (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <LanguageSwitcher />
              <ThemeToggle />
              <IconButton
                color="inherit"
                onClick={() => setDrawerOpen(true)}
                aria-label={t("aria.openNav")}
              >
                <MenuIcon />
              </IconButton>
            </Stack>
          )}
        </Toolbar>
      </Container>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 280, pt: 2 } }}
      >
        <Box role="presentation">
          <Stack spacing={1} sx={{ p: 2 }}>
            {NAV_ANCHORS.map((item) => (
              <Button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                size="large"
                sx={{ justifyContent: "flex-start" }}
              >
                {t(item.key)}
              </Button>
            ))}
          </Stack>
        </Box>
      </Drawer>
    </AppBar>
  );
}
